import { useEffect, useMemo, useRef } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Italic, Link2, List, ListOrdered } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface InvoiceEmailCustomBodyEditorProps {
  initialHtml: string;
  onHtmlChange: (html: string) => void;
  onBlur: () => void;
  disabled?: boolean;
  hasError?: boolean;
  placeholder: string;
  editorId: string;
  toolbarAriaLabel: string;
}

export function InvoiceEmailCustomBodyEditor({
  initialHtml,
  onHtmlChange,
  onBlur,
  disabled = false,
  hasError = false,
  placeholder,
  editorId,
  toolbarAriaLabel,
}: InvoiceEmailCustomBodyEditorProps): JSX.Element {
  const onHtmlChangeRef = useRef(onHtmlChange);
  const onBlurRef = useRef(onBlur);
  onHtmlChangeRef.current = onHtmlChange;
  onBlurRef.current = onBlur;

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        horizontalRule: false,
        strike: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
          class: "text-primary underline underline-offset-2",
        },
      }),
      Placeholder.configure({ placeholder }),
    ],
    [placeholder]
  );

  const editor = useEditor(
    {
      extensions,
      content: initialHtml,
      editable: !disabled,
      shouldRerenderOnTransaction: true,
      editorProps: {
        attributes: {
          id: editorId,
          "aria-multiline": "true",
          role: "textbox",
          class: cn(
            "max-w-none px-3 py-2 text-sm text-foreground outline-none",
            "[&_h1]:mb-2 [&_h1]:mt-3 [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:text-foreground",
            "[&_h2]:mb-2 [&_h2]:mt-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-foreground",
            "[&_h3]:mb-1 [&_h3]:mt-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-foreground",
            "[&_p]:my-2 [&_p]:leading-relaxed",
            "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6",
            "[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6",
            "[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-muted-foreground/40 [&_blockquote]:pl-3 [&_blockquote]:italic"
          ),
        },
        handleDOMEvents: {
          blur: () => {
            onBlurRef.current();
            return false;
          },
        },
      },
      onUpdate: ({ editor: ed }) => {
        onHtmlChangeRef.current(ed.getHTML());
      },
    },
    [extensions]
  );

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) {
    return (
      <div
        className={cn(
          "min-h-[120px] rounded-md border border-input bg-muted/30",
          hasError && "border-destructive"
        )}
        aria-hidden
      />
    );
  }

  return (
    <div
      className={cn(
        "overflow-hidden rounded-md border border-input bg-white dark:bg-[var(--dk-surface)] transition-colors duration-150",
        "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ring-offset-background",
        hasError && "border-destructive focus-within:ring-destructive"
      )}
    >
      <div
        className="flex flex-wrap gap-0.5 border-b border-border bg-muted/40 px-1 py-1"
        role="toolbar"
        aria-label={toolbarAriaLabel}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 transition-colors duration-150"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          aria-pressed={editor.isActive("heading", { level: 1 })}
          aria-label="Heading 1"
        >
          <span className="text-xs font-semibold">H1</span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 transition-colors duration-150"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          aria-pressed={editor.isActive("heading", { level: 2 })}
          aria-label="Heading 2"
        >
          <span className="text-xs font-semibold">H2</span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 transition-colors duration-150"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          aria-pressed={editor.isActive("heading", { level: 3 })}
          aria-label="Heading 3"
        >
          <span className="text-xs font-semibold">H3</span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 transition-colors duration-150"
          onClick={() => editor.chain().focus().toggleBold().run()}
          aria-pressed={editor.isActive("bold")}
          aria-label="Bold"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 transition-colors duration-150"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          aria-pressed={editor.isActive("italic")}
          aria-label="Italic"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 transition-colors duration-150"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          aria-pressed={editor.isActive("bulletList")}
          aria-label="Bullet list"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 transition-colors duration-150"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          aria-pressed={editor.isActive("orderedList")}
          aria-label="Numbered list"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 transition-colors duration-150"
          onClick={() => {
            const previousUrl = editor.getAttributes("link").href as
              | string
              | undefined;
            const url = window.prompt("URL", previousUrl ?? "https://");
            if (url === null) return;
            const trimmed = url.trim();
            if (trimmed === "") {
              editor.chain().focus().extendMarkRange("link").unsetLink().run();
              return;
            }
            editor
              .chain()
              .focus()
              .extendMarkRange("link")
              .setLink({ href: trimmed })
              .run();
          }}
          aria-pressed={editor.isActive("link")}
          aria-label="Link"
        >
          <Link2 className="h-4 w-4" />
        </Button>
      </div>
      <EditorContent
        editor={editor}
        className="[&_.ProseMirror]:min-h-[120px] [&_.ProseMirror]:resize-y [&_.ProseMirror]:max-h-[min(360px,50vh)] [&_.ProseMirror]:overflow-y-auto"
      />
    </div>
  );
}
